import React, { useEffect, useState } from 'react';
import { Modal, Button, Box, Typography } from '@availity/element';
import { getPortalBackendBaseUrl } from '../../../hooks/apiUrlResolver';
import { colors } from '../../../static/constants/theme';
import { themeColors } from '../../../static/constants/styles';
import { typography } from '../../../static/constants/typography';

interface Props {
  open: boolean;
  onClose: () => void;
  theme: 'light' | 'dark';
}

const GenerateDemoKeyModal = ({ open, onClose, theme }: Props): JSX.Element => {
  const [key, setKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (open) generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const generate = async () => {
    setLoading(true);
    setError(null);
    setKey(null);
    setCopied(false);
    try {
      const res = await fetch(`${getPortalBackendBaseUrl()}/keys/demo/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ gateway: 'unified-gateway' }),
      });
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      const data = await res.json();
      setKey(data.key);
    } catch (error_: unknown) {
      setError(error_ instanceof Error ? error_.message : 'Failed to generate key');
    } finally {
      setLoading(false);
    }
  };

  const copy = () => {
    if (key) {
      navigator.clipboard.writeText(key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleClose = () => {
    setKey(null);
    setError(null);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      data-testid="generate-demo-key-modal"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '& .MuiBackdrop-root': { backgroundColor: colors.background.backdrop },
      }}
    >
      <Box
        sx={{
          background: themeColors[theme].background,
          color: themeColors[theme].text,
          border: `1px solid ${themeColors[theme].border}`,
          borderRadius: '10px',
          padding: '32px 28px',
          minWidth: 340,
          maxWidth: 480,
          width: '90%',
          position: 'relative',
          outline: 'none',
        }}
      >
        <Button
          onClick={handleClose}
          aria-label="Close"
          sx={{
            position: 'absolute',
            top: 12,
            right: 16,
            minWidth: 'unset',
            background: 'none',
            color: themeColors[theme].text,
            fontSize: 20,
            padding: '0 4px',
            lineHeight: 1,
            boxShadow: 'none',
            '&:hover': { background: 'none', boxShadow: 'none' },
          }}
        >
          &times;
        </Button>

        <Typography sx={{ ...typography.montSemibold20, color: themeColors[theme].text, mb: 1 }}>
          Generate Demo API Key
        </Typography>
        <Typography sx={{ ...typography.latoRegular14, color: themeColors[theme].textMuted, mb: 3 }}>
          Creates a short-lived demo key for the Unified Gateway. Valid for 40 minutes.
        </Typography>

        {loading && (
          <Typography sx={{ ...typography.latoRegular14, color: themeColors[theme].textMuted }}>
            Generating&hellip;
          </Typography>
        )}

        {error && (
          <Box>
            <Typography sx={{ ...typography.latoRegular14, color: colors.text.error, mt: 0.5 }}>
              {error}
            </Typography>
            <Button onClick={generate} sx={{ mt: 1.5 }}>
              Retry
            </Button>
          </Box>
        )}

        {key && (
          <Box sx={{ mt: 0.5 }}>
            <Typography sx={{ ...typography.latoRegular14, color: themeColors[theme].textMuted, mb: 1 }}>
              Your demo key (shown once):
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                background: themeColors[theme].codeBackground,
                borderRadius: '6px',
                padding: '10px 12px',
                border: `1px solid ${themeColors[theme].border}`,
              }}
            >
              <Box
                component="code"
                sx={{ flex: 1, fontSize: 13, wordBreak: 'break-all', color: themeColors[theme].codeText, fontFamily: 'monospace' }}
              >
                {key}
              </Box>
              <Button onClick={copy} size="small">
                {copied ? 'Copied!' : 'Copy'}
              </Button>
            </Box>
            <Typography sx={{ ...typography.latoRegular14, color: themeColors[theme].textSubtle, mt: 1, fontSize: 12 }}>
              Store this key securely &mdash; it won&apos;t be shown again.
            </Typography>
          </Box>
        )}
      </Box>
    </Modal>
  );
};

export default GenerateDemoKeyModal;
